import { Badge } from '../../ui/Badge'
import { serviceAccess, type IngressState, type PortAccess, type SentPort } from '../../core/access'
import { AccessBlock, ConnectionDetails, CopyField } from './ServiceAccess'

/**
 * A provider's lease status, read as the tenant's machine rather than as JSON.
 *
 * The gateway's `GET /lease/{dseq}/{gseq}/{oseq}/status` answers with the
 * upstream `LeaseStatus`: `services` keyed by service name (replica counts and
 * the URIs its ingress serves), `forwarded_ports` keyed by service name, and,
 * when the cluster leases IPs, `ips`. Every field is read defensively — this is
 * another machine's answer — and a field that is absent is left out rather than
 * shown as zero.
 *
 * A provider that diagnoses its workloads also sends `problems`, keyed by
 * service: why a service is not running as asked (provider
 * `cluster/problems.go`). Replica counts alone cannot say that — an image that
 * can never be pulled and one about to start both read `0 of 1` — so health is
 * its own status here, not a relabelled replica count.
 */

/** The provider's stable problem kinds. Copy keys on these, never on `reason`. */
export type ProblemKind = 'image-pull' | 'crash' | 'out-of-memory' | 'config' | 'unschedulable' | 'not-ready' | 'other'

const PROBLEM_KINDS: readonly ProblemKind[] = ['image-pull', 'crash', 'out-of-memory', 'config', 'unschedulable', 'not-ready', 'other']

export interface ProblemView {
  kind: ProblemKind
  reason: string
  message: string
  container: string
  since: string | null
  restarts: number | null
  exitCode: number | null
}

export interface ServiceView {
  name: string
  ready: number | null
  total: number | null
  uris: string[]
  problems: ProblemView[]
}

export interface EndpointView {
  service: string
  /** `host:externalPort`, as a tenant connects to it. */
  address: string
  /** `port/proto`, the container side. */
  target: string
  host: string
  /** The container port, or null when the host did not say. */
  port: number | null
  /** The public port the host chose (a NodePort), never the SDL's `as:`. */
  externalPort: number | null
  /** Lower-case `tcp` or `udp`. */
  proto: string
}

function portNumber(value: unknown): number | null {
  const number = typeof value === 'number' ? value : typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : NaN
  return Number.isInteger(number) && number > 0 && number <= 65535 ? number : null
}

export interface LeaseStatusModel {
  services: ServiceView[]
  endpoints: EndpointView[]
  /** The host sent `problems` at all. False means an older provider that cannot say why. */
  problemsReported: boolean
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null
}

function count(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : typeof value === 'number' ? String(value) : ''
}

function problemView(raw: unknown): ProblemView | null {
  const item = record(raw)
  if (!item) return null
  const reason = text(item.reason)
  const kindText = text(item.kind)
  if (!kindText && !reason) return null
  // An unknown kind is still a problem; it is shown with the generic copy.
  const kind = (PROBLEM_KINDS as readonly string[]).includes(kindText) ? kindText as ProblemKind : 'other'
  return { kind, reason, message: text(item.message), container: text(item.container), since: text(item.since) || null, restarts: count(item.restarts), exitCode: count(item.exit_code) }
}

/** The readable model, or null when the payload is not a lease status at all. */
export function leaseStatusModel(value: unknown): LeaseStatusModel | null {
  const root = record(value)
  if (!root) return null
  const problemsRoot = record(root.problems)
  const problemsFor = (service: string): ProblemView[] => {
    const list = problemsRoot?.[service]
    return Array.isArray(list) ? list.map(problemView).filter((problem): problem is ProblemView => problem !== null) : []
  }
  const services = Object.entries(record(root.services) ?? {}).map(([key, raw]): ServiceView => {
    const item = record(raw) ?? {}
    return {
      name: text(item.name) || key,
      ready: count(item.ready_replicas) ?? count(item.available),
      total: count(item.total) ?? count(item.replicas),
      uris: Array.isArray(item.uris) ? item.uris.filter((uri): uri is string => typeof uri === 'string' && uri.length > 0) : [],
      problems: problemsFor(key),
    }
  })
  // A service with a problem but no status entry of its own is still shown.
  for (const key of Object.keys(problemsRoot ?? {})) {
    const problems = problemsFor(key)
    if (problems.length > 0 && !services.some((service) => service.name === key)) {
      services.push({ name: key, ready: null, total: null, uris: [], problems })
    }
  }
  const endpoints: EndpointView[] = []
  for (const [service, list] of Object.entries(record(root.forwarded_ports) ?? {})) {
    if (!Array.isArray(list)) continue
    for (const raw of list) {
      const port = record(raw)
      if (!port) continue
      const host = text(port.host)
      const external = text(port.externalPort)
      if (!host || !external) continue
      const proto = text(port.proto).toLowerCase() || 'tcp'
      endpoints.push({ service: text(port.name) || service, address: `${host}:${external}`, target: `${text(port.port)}/${proto}`, host, port: portNumber(port.port), externalPort: portNumber(port.externalPort), proto })
    }
  }
  for (const [service, list] of Object.entries(record(root.ips) ?? {})) {
    if (!Array.isArray(list)) continue
    for (const raw of list) {
      const lease = record(raw)
      if (!lease) continue
      const ip = text(lease.ip)
      const external = text(lease.external_port)
      if (!ip || !external) continue
      const proto = text(lease.protocol).toLowerCase() || 'tcp'
      endpoints.push({ service, address: `${ip}:${external}`, target: `${text(lease.port)}/${proto}`, host: ip, port: portNumber(lease.port), externalPort: portNumber(lease.external_port), proto })
    }
  }
  if (services.length === 0 && endpoints.length === 0) return null
  return { services, endpoints, problemsReported: problemsRoot !== null }
}

export type Health = 'running' | 'attention' | 'starting' | 'not-reporting' | 'reported'

const HEALTH_BADGE: Record<Health, { tone: 'positive' | 'caution' | 'neutral'; label: string }> = {
  running: { tone: 'positive', label: 'Running' },
  attention: { tone: 'caution', label: 'Needs attention' },
  starting: { tone: 'neutral', label: 'Starting' },
  'not-reporting': { tone: 'caution', label: 'Not reporting' },
  reported: { tone: 'neutral', label: 'Reported' },
}

/**
 * One service's health. `overdue` is true once the workload has had longer
 * than it normally takes to start (the caller owns the clock); only then is a
 * host that cannot report problems shown as Not reporting rather than Starting.
 */
export function serviceHealth(service: ServiceView, { problemsReported, overdue }: { problemsReported: boolean; overdue: boolean }): Health {
  if (service.problems.length > 0) return 'attention'
  if (service.ready === null || service.total === null) return 'reported'
  if (service.total > 0 && service.ready >= service.total) return 'running'
  return !problemsReported && overdue ? 'not-reporting' : 'starting'
}

/** The whole lease, worst service first: what the overview says in one line. */
export function leaseHealth(model: LeaseStatusModel, overdue: boolean): { health: Health; problem: ProblemView | null } {
  const order: Health[] = ['attention', 'not-reporting', 'starting', 'reported', 'running']
  const healths = model.services.map((service) => serviceHealth(service, { problemsReported: model.problemsReported, overdue }))
  const health = order.find((candidate) => healths.includes(candidate)) ?? 'reported'
  const problem = model.services.flatMap((service) => service.problems)[0] ?? null
  return { health, problem }
}

/** The image a pull failure names, e.g. `okolos/ai-rep:latest`, when the message quotes it. */
function pulledImage(message: string): string | null {
  const quoted = /image\s+"([^"]+)"/i.exec(message)
  return quoted?.[1] ?? null
}

/** One plain sentence and what to do, keyed on the provider's `kind`. */
export function problemSentence(problem: ProblemView): string {
  switch (problem.kind) {
    case 'image-pull': {
      const image = pulledImage(problem.message)
      return `The host cannot download ${image ? image : 'your container image'}. Check the image name and tag, make the image public, or add registry credentials in the SDL editor.`
    }
    case 'crash':
      return `Your container keeps stopping${problem.exitCode !== null ? ` (exit code ${problem.exitCode})` : ''}. Check its start command and settings.`
    case 'out-of-memory':
      return 'Your container ran out of memory. Rent more memory, or reduce what it uses.'
    case 'config':
      return "The host could not create the container from its configuration."
    case 'unschedulable':
      return "The host does not have room for this workload right now."
    case 'not-ready':
      return 'Your container is running but not answering its health check.'
    default:
      return 'The host reports a problem starting this container.'
  }
}

/** Shown for a host that cannot report problems, once the start is overdue. */
export const NOT_REPORTING_COPY = "This is taking longer than usual, and this host does not report why. Check that the image name is right and the image is public."


/**
 * The link a host-reported URI becomes, or null when it must not be one.
 *
 * The ingress reports a bare hostname, and a forwarded port is `host:port`;
 * both are opened over http, as they are served. Anything else must be an
 * explicit http or https URI: the value comes from the host, and React 18
 * renders any other scheme, `javascript:` included, as given. The Access tab
 * and the App tab both link through this.
 *
 * `provider.example.test:32080` reads like a scheme to a URL parser, so a
 * bare host is recognised first, by shape: DNS labels or an IPv4 address,
 * then at most a numeric port and a path. Nothing with a scheme matches it.
 */
const BARE_HOST = /^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)*(?::\d{1,5})?(?:[/?#].*)?$/i

export function serviceHref(uri: string): string | null {
  const value = uri.trim()
  if (value === '' || /[\s"'<>\\]/.test(value)) return null
  const candidate = BARE_HOST.test(value) ? `http://${value}` : value
  try {
    const url = new URL(candidate)
    // The value as reported, once it has parsed as http(s): a check, not a rewrite.
    return (url.protocol === 'http:' || url.protocol === 'https:') && url.hostname !== '' ? candidate : null
  } catch {
    return null
  }
}

/**
 * The cluster's public address, as the provider's own `GET /status` reports
 * it (`cluster_public_hostname`, the provider's `--cluster-public-hostname`).
 * It is the same host the provider puts on every forwarded port, and the one
 * its ingress controller answers on. Not the gateway's `host_uri`: that is the
 * management API, which need not be where workloads are served.
 *
 * Null when the provider does not report one. A value carrying a scheme or a
 * port is reduced to the host, and anything else that is not a bare host is
 * refused rather than shown.
 */
export function clusterPublicHost(status: unknown): string | null {
  const raw = record(status)?.cluster_public_hostname
  if (typeof raw !== 'string' || raw.trim() === '') return null
  const value = raw.trim()
  try {
    const host = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `http://${value}`).hostname
    return host ? host.replace(/^\[(.*)\]$/, '$1') : null
  } catch {
    return null
  }
}

function ProblemDetail({ problem }: { problem: ProblemView }) {
  return <div className="lease-problem">
    <p className="lease-problem-sentence">{problemSentence(problem)}</p>
    <details className="technical-disclosure">
      <summary>Technical details</summary>
      <dl className="review-list">
        {problem.reason && <div><dt>Reason</dt><dd><code>{problem.reason}</code></dd></div>}
        {problem.container && <div><dt>Container</dt><dd>{problem.container}</dd></div>}
        {problem.restarts !== null && problem.restarts > 0 && <div><dt>Restarts</dt><dd>{problem.restarts}</dd></div>}
        {problem.since && <div><dt>Since</dt><dd>{problem.since}</dd></div>}
      </dl>
      {problem.message && <pre className="lease-problem-message">{problem.message}</pre>}
    </details>
  </div>
}

/** "1 of 1 replicas ready", or that the host did not say. */
export function replicasText(service: ServiceView): string {
  return service.ready !== null && service.total !== null ? `${service.ready} of ${service.total} ${service.total === 1 ? 'replica' : 'replicas'} ready` : 'Replicas not reported'
}

/**
 * The access card, one section per service: its status once (the count and
 * the pill), why it is not running if the host says, every way in, and the
 * connection details behind them (INSTANCE-ACCESS-AND-LEASE-VIEW-PLAN §2.1).
 */
export function LeaseStatusView({ value, overdue = false, ingressHost = null, https = false, ingress = https ? 'https' : 'not-public', sentPorts = null }: {
  value: unknown
  overdue?: boolean
  /** The provider-reported cluster address (`clusterPublicHost`); null when not reported. */
  ingressHost?: string | null
  /** The host advertises HTTPS ingress on chain (`ingressServesHttps`). */
  https?: boolean
  /** What is known about the host's HTTPS, for the copy beside a name it does not serve. */
  ingress?: IngressState
  /** The global ports each service was sent with, by service name; null when the SDL is not in this browser. */
  sentPorts?: Record<string, SentPort[]> | null
}) {
  const model = leaseStatusModel(value)
  if (!model) return <p className="field-help">The host answered, but not with a lease status this console can read.</p>
  return <div className="lease-status">
    {model.services.map((service) => {
      const health = serviceHealth(service, { problemsReported: model.problemsReported, overdue })
      const badge = HEALTH_BADGE[health]
      const accesses = serviceAccess(service, model.endpoints, { https, sent: sentPorts?.[service.name] ?? null })
      const hostIp = ingressHost ?? accesses.find((access): access is Extract<PortAccess, { kind: 'nodeport' }> => access.kind === 'nodeport')?.ip ?? null
      return <section key={service.name} className="lease-service" aria-label={`Service ${service.name}`}>
        <div className="lease-service-head"><strong>{service.name}</strong><span className="muted lease-service-replicas">{replicasText(service)}</span><Badge tone={badge.tone}>{badge.label}</Badge></div>
        {service.problems.map((problem, index) => <ProblemDetail key={`${problem.kind}-${problem.container}-${index}`} problem={problem} />)}
        {health === 'not-reporting' && <p className="lease-problem-sentence">{NOT_REPORTING_COPY}</p>}
        {accesses.length === 0
          ? <p className="field-help">This service has no public address or port.</p>
          : accesses.map((access, index) => <AccessBlock key={`${access.kind}-${index}`} access={access} ingress={ingress} />)}
        {accesses.length > 0 && <ConnectionDetails accesses={accesses} hostIp={hostIp} />}
      </section>
    })}
    {/* Ports whose service reported no status of its own still belong on screen. */}
    {model.endpoints.filter((endpoint) => !model.services.some((service) => service.name === endpoint.service)).map((endpoint) => <section key={`${endpoint.service}-${endpoint.address}`} className="lease-service" aria-label={`Service ${endpoint.service}`}>
      <div className="lease-service-head"><strong>{endpoint.service}</strong></div>
      <CopyField value={endpoint.address} label={`Copy ${endpoint.address}`} />
      <p className="field-help">Port {endpoint.target}</p>
    </section>)}
  </div>
}
